import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import ProjectCard from "./ProjectCard";
import projectsData from "../data/projects.json";

// Register GSAP Plugin
gsap.registerPlugin(ScrollTrigger);

// --- MOBILE COMPONENT (Native Smooth Scroll - NO GSAP) ---
const MobileProjects = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const cardRefs = useRef([]);

  // Use Intersection Observer to determine which card is currently visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardRefs.current.indexOf(entry.target);
            if (index !== -1) {
              setActiveIndex(index);
            }
          }
        });
      },
      {
        root: containerRef.current,
        threshold: 0.6, // Card must be 60% visible to trigger background change
      },
    );

    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="min-h-screen relative flex flex-col justify-center overflow-hidden border-t border-white/5 py-24">
      {/* 1. Mobile Title Area */}
      <div className="px-6 mb-12 relative z-20">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-4"
        >
          <div className="h-px w-8 bg-purple-500"></div>
          <span className="text-purple-400 font-medium tracking-widest uppercase text-xs">
            Portfolio
          </span>
        </motion.div>

        <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-2 leading-tight">
          Featured <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
            Projects
          </span>
        </h2>
      </div>

      {/* 2. Atmospheric Background Keyword (Updates natively via Observer) */}
      <div className="absolute inset-0 overflow-hidden flex items-center justify-center opacity-[0.03] select-none pointer-events-none z-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.4 }}
            className="text-[25vw] font-black tracking-tighter whitespace-nowrap origin-left rotate-[-90deg] absolute left-[15%] top-[40%]"
          >
            {projectsData[activeIndex]?.bgKeyword}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* 3. Native Horizontal Swipe Container */}
      <div
        ref={containerRef}
        className="w-full overflow-x-auto flex gap-6 px-6 pb-12 pt-4 snap-x snap-mandatory relative z-20"
        style={{
          scrollbarWidth: "none", // Hide scrollbar Firefox
          msOverflowStyle: "none", // Hide scrollbar IE
        }}
      >
        {/* Hide scrollbar Webkit */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
          div::-webkit-scrollbar { display: none; }
        `,
          }}
        />

        {projectsData.map((project, index) => (
          <div
            key={index}
            ref={(el) => (cardRefs.current[index] = el)}
            className="snap-center shrink-0 w-[85vw] sm:w-[350px] relative transition-transform duration-500"
          >
            <ProjectCard project={project} />
          </div>
        ))}

        {/* Spacer to allow the last card to reach the center */}
        <div className="shrink-0 w-[10vw]"></div>
      </div>

      {/* 4. Simple Pagination Dots */}
      <div className="flex justify-center gap-2 mt-4 relative z-20">
        {projectsData.map((_, idx) => (
          <div
            key={idx}
            className={`h-1.5 rounded-full transition-all duration-300 ${activeIndex === idx ? "w-6 bg-purple-500" : "w-1.5 bg-white/20"}`}
          />
        ))}
      </div>
    </section>
  );
};

// --- DESKTOP COMPONENT (GSAP ScrollTrigger) ---
const DesktopProjects = () => {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    let ctx = gsap.context(() => {
      const track = trackRef.current;
      const cards = gsap.utils.toArray(".project-card");

      const getScrollAmount = () => track.scrollWidth - window.innerWidth;

      const tween = gsap.to(track, {
        x: () => -getScrollAmount(),
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: () => `+=${getScrollAmount()}`,
          pin: true,
          scrub: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const progress = self.progress;
            const index = Math.round(progress * (projectsData.length - 1));
            if (index !== activeIndex) setActiveIndex(index);

            cards.forEach((card, i) => {
              const cardCenter = card.offsetLeft + card.offsetWidth / 2;
              const viewportCenter =
                window.innerWidth / 2 - track.getBoundingClientRect().left;
              const distance = cardCenter - viewportCenter;
              const maxDistance = window.innerWidth / 2;

              let normalizedDistance = Math.abs(distance) / maxDistance;
              normalizedDistance = Math.min(1, Math.max(0, normalizedDistance));

              const scale = 1.05 - 0.2 * normalizedDistance;
              const opacity = 1 - 0.7 * normalizedDistance;
              const rotateZ = (distance / maxDistance) * 8;

              gsap.set(card, {
                scale: scale,
                opacity: opacity,
                rotationZ: rotateZ,
                boxShadow:
                  normalizedDistance < 0.2
                    ? `0 0 60px ${projectsData[i].colorGlow}`
                    : "0 0 0px rgba(0,0,0,0)",
                zIndex: normalizedDistance < 0.2 ? 10 : 1,
              });
            });
          },
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [activeIndex]);

  return (
    // ✨ THE FIX: Extra wrapper div added here to protect React's DOM tree from GSAP's pin-spacer
    <div className="w-full relative">
      <section
        ref={sectionRef}
        className="h-screen relative flex items-center overflow-hidden border-t border-white/5"
      >
        <div className="absolute left-0 top-0 w-[45vw] h-full p-16 flex flex-col justify-center pointer-events-none z-10">
          <div className="absolute inset-0 overflow-hidden flex items-center justify-center opacity-[0.03] select-none">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="text-[12vw] font-black tracking-tighter whitespace-nowrap origin-left rotate-[-90deg] absolute left-[10%]"
              >
                {projectsData[activeIndex]?.bgKeyword}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="relative" style={{ zIndex: 10000 }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-4"
            >
              <div className="h-px w-8 bg-purple-500"></div>
              <span className="text-purple-400 font-medium tracking-widest uppercase text-sm">
                Portfolio
              </span>
            </motion.div>

            <h2 className="text-5xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              Featured <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
                Projects
              </span>
            </h2>

            <p className="text-slate-400 text-lg max-w-md leading-relaxed">
              Real-world products I've built, <br /> problems I've solved,{" "}
              <br />
              and digital experiences I've crafted.
            </p>
          </div>
        </div>

        <div className="absolute left-0 top-0 h-full w-full pointer-events-none z-20">
          <div
            ref={trackRef}
            className="flex h-full items-center gap-20 w-max px-[calc(50vw-210px)] pointer-events-auto"
          >
            {projectsData.map((project, index) => (
              <ProjectCard key={index} project={project} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// --- MAIN EXPORT W/ RESIZE LISTENER ---
const FeaturedProjects = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768); // 768px is standard md breakpoint
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Return the correct layout based on screen size
  return isMobile ? <MobileProjects /> : <DesktopProjects />;
};

export default FeaturedProjects;
